// frontend/src/pages/TareasVisuales.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';
import PostIt from '../components/PostIt';
import StepCarousel from '../components/StepCarousel';
import './TareasVisuales.css';
import { useNotification } from '../context/NotificationContext';
import { useI18n } from '../i18n';
import { normalizeTask, normalizeTaskList } from '../utils/taskPlaceNormalizer';

const TASK_SELECTOR_LIMIT = 200;

const TareasVisuales = () => {
  const { addNotification, addConfirm, addPrompt } = useNotification();
  const { t } = useI18n();
  const location = useLocation();
  const canvasRef = useRef(null);
  const visualTasksUiRef = useRef(null);
  const [places, setPlaces] = useState([]);
  const [deletedPlaces, setDeletedPlaces] = useState([]);
  const [currentPlace, setCurrentPlace] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [placesCollapsed, setPlacesCollapsed] = useState(false);
  const [newPlaceName, setNewPlaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskSelectorError, setTaskSelectorError] = useState('');
  const [createTaskError, setCreateTaskError] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskSelectorTotal, setTaskSelectorTotal] = useState(0);
  const [isCreatingPlaceTask, setIsCreatingPlaceTask] = useState(false);
  const [pendingTaskToAssign, setPendingTaskToAssign] = useState(null);
  const [loadingDeletedPlaces, setLoadingDeletedPlaces] = useState(false);

  useEffect(() => {
    if (selectedTask) {
      document.body.classList.add('task-focus-mode');
    } else {
      document.body.classList.remove('task-focus-mode');
    }
    return () => document.body.classList.remove('task-focus-mode');
  }, [selectedTask]);

  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      if (location.state.pendingTaskId) {
        setPendingTaskToAssign(location.state.pendingTaskId);
      }
      // Limpiar el estado para que no se reabra al recargar
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchPlaces = useCallback(async (preferredPlaceId = null) => {
    try {
      const res = await api.get('/places');
      if (res.data.ok) {
        const nextPlaces = res.data.places || [];
        setPlaces(nextPlaces);
        if (nextPlaces.length > 0) {
          const targetPlaceId = preferredPlaceId || nextPlaces[0].id;
          fetchPlaceTasks(targetPlaceId);
        } else {
          setCurrentPlace(null);
        }
        return nextPlaces;
      }
    } catch (err) {
      console.error('Error fetching places:', err);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlaceTasks = async (placeId) => {
    try {
      const res = await api.get(`/places/${placeId}/tasks`);
      if (res.data.ok) {
        const place = res.data.place || {};
        setCurrentPlace({
          ...place,
          tasks: normalizeTaskList(place.tasks || [])
        });
        setEditName(place.name || '');
      }
    } catch (err) {
      console.error('Error fetching place tasks:', err);
    }
  };

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  const handleCreatePlace = async () => {
    if (!newPlaceName.trim() || isCreating) return;
    setIsCreating(true);
    try {
      const res = await api.post('/places', { name: newPlaceName });
      if (res.data.ok) {
        setPlaces([res.data.place, ...places]);
        setCurrentPlace(res.data.place);
        setEditName(res.data.place.name);
        setShowCreateModal(false);
        setNewPlaceName('');

        if (pendingTaskToAssign) {
          try {
            await api.put(`/tareas/${pendingTaskToAssign}`, {
              place_id: res.data.place.id
            });
            setPendingTaskToAssign(null);
            await refreshCurrentPlace(res.data.place.id);
          } catch (assignErr) {
            console.error("Error assigning pending task:", assignErr);
          }
        }
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('places.createError'), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const fetchDeletedPlaces = async () => {
    setLoadingDeletedPlaces(true);
    try {
      const res = await api.get('/places/deleted');
      if (res.data.ok) {
        setDeletedPlaces(res.data.places || []);
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('places.trashLoadError'), 'error');
    } finally {
      setLoadingDeletedPlaces(false);
    }
  };

  const handleOpenTrash = () => {
    setShowTrashModal(true);
    fetchDeletedPlaces();
  };

  const handleDeletePlace = async (place) => {
    if (!place) return;
    addConfirm(t('places.deleteConfirm', { name: place.name }), async () => {
      try {
        const res = await api.delete(`/places/${place.id}`);
        if (res.data.ok) {
          const remainingPlaces = places.filter(p => p.id !== place.id);
          setPlaces(remainingPlaces);
          setDeletedPlaces(prev => [res.data.place, ...prev.filter(p => p.id !== place.id)]);
          addNotification(t('places.deleted'), 'success');

          if (currentPlace?.id === place.id) {
            if (remainingPlaces.length > 0) {
              fetchPlaceTasks(remainingPlaces[0].id);
            } else {
              setCurrentPlace(null);
            }
          }
        }
      } catch (err) {
        addNotification(err.response?.data?.error || t('places.deleteError'), 'error');
      }
    });
  };

  const handleRestorePlace = async (place) => {
    if (!place) return;
    try {
      const res = await api.post(`/places/${place.id}/restore`);
      if (res.data.ok) {
        setDeletedPlaces(deletedPlaces.filter(p => p.id !== place.id));
        setPlaces([res.data.place, ...places.filter(p => p.id !== place.id)]);
        setCurrentPlace(res.data.place);
        setEditName(res.data.place.name);
        setShowTrashModal(false);
        fetchPlaceTasks(res.data.place.id);
        addNotification(t('places.restored'), 'success');
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('places.restoreError'), 'error');
    }
  };

  const handleRegenerateImage = async () => {
    if (!currentPlace || isRegenerating) return;
    addConfirm(t('places.regenerateConfirm'), async () => {
      setIsRegenerating(true);
      try {
        const res = await api.post(`/places/${currentPlace.id}/regenerate`);
        if (res.data.ok) {
          setCurrentPlace(res.data.place);
        }
      } catch (err) {
        addNotification(err.response?.data?.error || t('places.regenerateError'), 'error');
      } finally {
        setIsRegenerating(false);
      }
    });
  };

  const handleUpdateName = async () => {
    if (!editName.trim() || editName === currentPlace.name) {
      setIsEditingName(false);
      return;
    }
    try {
      const res = await api.put(`/places/${currentPlace.id}`, { name: editName });
      if (res.data.ok) {
        setCurrentPlace(res.data.place);
        setPlaces(places.map(p => p.id === res.data.place.id ? res.data.place : p));
        setIsEditingName(false);
      }
    } catch (err) {
      addNotification(t('places.updateNameError'), "error");
    }
  };

  const refreshCurrentPlace = async (placeId = currentPlace?.id) => {
    if (!placeId) return;
    await fetchPlaces(placeId);
  };

  const fetchAllTasks = async ({ q = taskSearch, offset = 0, append = false } = {}) => {
    if (!currentPlace?.id) return;
    setLoadingTasks(true);
    setTaskSelectorError('');
    try {
      const res = await api.get('/tareas/assignable-to-place', {
        params: {
          placeId: currentPlace.id,
          q: q || undefined,
          limit: TASK_SELECTOR_LIMIT,
          offset
        }
      });
      if (res.data.ok) {
        const normalizedItems = normalizeTaskList(res.data.items || []);
        setAllTasks(prev => append ? normalizeTaskList([...prev, ...normalizedItems]) : normalizedItems);
        setTaskSelectorTotal(Number(res.data.total) || normalizedItems.length);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTaskSelectorError(err.response?.data?.error || t('tasks.loadError'));
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleQuickAddTask = async () => {
    setTaskSearch('');
    setAllTasks([]);
    setTaskSelectorTotal(0);
    setTaskSelectorError('');
    setCreateTaskError('');
    setShowTaskSelector(true);
    fetchAllTasks({ q: '', offset: 0 });
  };

  const handleAssignTask = async (task) => {
    const normalizedTask = normalizeTask(task);
    if (!normalizedTask?.id || !currentPlace?.id) return;
    if (String(normalizedTask.place_id) === String(currentPlace.id)) return;
    try {
      await api.put(`/tareas/${normalizedTask.id}`, {
        place_id: currentPlace.id
      });
      await refreshCurrentPlace(currentPlace.id);
      setShowTaskSelector(false);
    } catch (err) {
      addNotification(t('places.assignError'), "error");
    }
  };

  const handleCreateAndAssign = async () => {
    if (!currentPlace?.id || isCreatingPlaceTask) return;
    addPrompt(t('places.promptNewTask'), "", async (taskName) => {
      if (!taskName || !taskName.trim()) return;
      setIsCreatingPlaceTask(true);
      setCreateTaskError('');
      try {
        const res = await api.get('/tareas/generate', {
          params: {
            prompt: taskName,
            place_id: currentPlace.id
          }
        });
        if (res.data.ok) {
          await refreshCurrentPlace(currentPlace.id);
          setShowTaskSelector(false);
        }
      } catch (err) {
        const message = err.response?.data?.error || t('places.generateTaskError');
        setCreateTaskError(message);
        addNotification(message, "error");
      } finally {
        setIsCreatingPlaceTask(false);
      }
    });
  };

  const handleRemoveTaskFromPlace = async (taskId) => {
    try {
      const res = await api.put(`/tareas/${taskId}`, { place_id: null });
      if (res.data.ok) {
        await refreshCurrentPlace(currentPlace.id);
      }
    } catch (err) {
      addNotification(t('places.removeTaskError'), "error");
      console.error(err);
    }
  };

  const handleTaskUpdate = (updatedTask) => {
    const normalizedTask = normalizeTask(updatedTask);
    if (!normalizedTask) return;
    setCurrentPlace(prevPlace => {
      if (!prevPlace || !prevPlace.tasks) return prevPlace;

      return {
        ...prevPlace,
        tasks: prevPlace.tasks.map(t => String(t.id) === String(normalizedTask.id) ? normalizedTask : t)
      };
    });

    if (selectedTask && String(selectedTask.id) === String(normalizedTask.id)) {
      setSelectedTask(normalizedTask);
    }
  };

  const handlePositionChange = async (taskId, newX, newY, bounds = {}) => {
    if (!currentPlace || !currentPlace.tasks) return;

    const task = currentPlace.tasks.find(t => t.id === taskId);
    if (!task) return;

    const minY = Number.isFinite(bounds.minY) ? bounds.minY : 0;
    const maxX = Number.isFinite(bounds.maxX) ? bounds.maxX : 100;
    const maxY = Number.isFinite(bounds.maxY) ? bounds.maxY : 100;
    const finalX = Math.max(0, Math.min(newX, maxX));
    const finalY = Math.max(minY, Math.min(newY, maxY));

    // Actualizar estado local inmediatamente para fluidez. No empujamos el Post-it
    // por colisiones con otros Post-its: el usuario debe conservar exactamente el
    // punto donde soltó, limitado solo por los bordes reales del canvas.
    const updatedMeta = {
      ...(task.meta || {}),
      visual_position: { x: finalX, y: finalY }
    };
    const updatedTask = { ...task, meta: updatedMeta };
    handleTaskUpdate(updatedTask);

    try {
      await api.put(`/tareas/${taskId}`, {
        meta: updatedMeta
      });
    } catch (err) {
      console.error("Error persisting position:", err);
    }
  };

  const visibleSelectorTasks = normalizeTaskList(allTasks);
  const selectorHasMore = visibleSelectorTasks.length < taskSelectorTotal;

  return (
    <div id="places-main" data-agent-page="places" className={`tareas-visuales-container ${selectedTask ? 'focus-mode' : ''}`}>
      {currentPlace?.background_image_url && (
        <div
          className="place-background"
          style={{ backgroundImage: `url(${currentPlace.background_image_url})` }}
        />
      )}
      <div className="place-overlay" />

      {!selectedTask && (
        <div className="visual-tasks-ui" ref={visualTasksUiRef}>
          <div id="places-list" className={`places-selector ${placesCollapsed ? 'collapsed' : ''}`}>
            <button
              className="btn-collapse-places"
              onClick={() => setPlacesCollapsed(!placesCollapsed)}
              title={placesCollapsed ? t('places.expandList') : t('places.collapseList')}
              aria-label={placesCollapsed ? t('places.expandList') : t('places.collapseList')}
            >
              {placesCollapsed ? '📍' : '‹'}
            </button>
            {!placesCollapsed && places.map(p => (
              <button
                key={p.id}
                className={`place-tab ${currentPlace?.id === p.id ? 'active' : ''}`} data-agent-place-id={String(p.id)} data-agent-place-name={p.name} data-agent-place-title={p.name} data-agent-action="open-place"
                onClick={() => fetchPlaceTasks(p.id)}
              >
                <span className="place-tab-name">{p.name}</span>
                <span
                  className="place-tab-delete"
                  role="button"
                  tabIndex={0}
                  title={t('places.sendToTrash')}
                  aria-label={t('places.sendNamedToTrash', { name: p.name })}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeletePlace(p);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      handleDeletePlace(p);
                    }
                  }}
                >
                  ×
                </span>
              </button>
            ))}
            <button id="btn-create-place" className="btn-add-place" onClick={() => setShowCreateModal(true)}>
              + {t('places.newPlace')}
            </button>
            <button className="btn-trash-place" onClick={handleOpenTrash} title={t('places.trash')}>
              🗑️
            </button>
          </div>

          {currentPlace && (
            <div id="place-current-detail" data-agent-readable="current-place" className="place-info-header">
              <div className="title-row">
                {isEditingName ? (
                  <input
                    className="edit-name-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleUpdateName}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                    autoFocus
                  />
                ) : (
                  <div className="title-stack">
                    <h1>{currentPlace.name}</h1>
                    {currentPlace.ai_subtitle && (
                      <h2 className="ai-subtitle">{currentPlace.ai_subtitle}</h2>
                    )}
                  </div>
                )}
                <div className="header-actions">
                  <button className="btn-action-icon" onClick={() => setIsEditingName(true)} title={t('places.editName')}>
                    ✏️
                  </button>
                  <button className="btn-action-icon" onClick={handleRegenerateImage} disabled={isRegenerating} title={t('places.regenerateImage')}>
                    {isRegenerating ? '...' : '🖼️'}
                  </button>
                  <button className="btn-action-icon" onClick={handleQuickAddTask} title={t('places.addTask')}>
                    ➕
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedTask && (
        <div className="postits-canvas" ref={canvasRef}>
          {currentPlace?.tasks && currentPlace.tasks.length > 0 &&
            currentPlace.tasks.map((task, idx) => (
              <PostIt
                key={task.id}
                task={task}
                index={idx}
                canvasRef={canvasRef}
                safeTopRef={visualTasksUiRef}
                onClick={() => setSelectedTask(task)}
                onRemove={handleRemoveTaskFromPlace}
                onPositionChange={handlePositionChange}
              />
            ))
          }
        </div>
      )}

      {currentPlace && (!currentPlace.tasks || currentPlace.tasks.length === 0) && !selectedTask && (
        <div className="empty-canvas-msg">
          <h3>{t('places.noTasksTitle')}</h3>
          <p>{t('places.noTasksBody', { name: currentPlace.name })}</p>
          <button className="btn-add-first" onClick={handleQuickAddTask}>{t('places.addFirstTask')}</button>
        </div>
      )}

      {selectedTask && (
        <StepCarousel
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onTaskUpdate={handleTaskUpdate}
        />
      )}

      {showTaskSelector && (
        <div className="modal-overlay">
          <div className="task-selector-modal glass-modal">
            <h2>{t('places.addTaskTo', { name: currentPlace?.name || '' })}</h2>
            <div className="task-selector-content">
              <button className="btn-create-new-task" onClick={handleCreateAndAssign} disabled={isCreatingPlaceTask}>
                {isCreatingPlaceTask ? t('places.creatingTask') : `+ ${t('places.createNewTask')}`}
              </button>
              {isCreatingPlaceTask && (
                <div className="place-task-creation-feedback" role="status" aria-live="polite">
                  <div className="spinner"></div>
                  <div>
                    <strong>{t('places.creatingTask')}</strong>
                    <span>{t('places.creatingTaskHint')}</span>
                  </div>
                </div>
              )}
              {createTaskError && (
                <p className="task-selector-state error place-task-create-error">{createTaskError}</p>
              )}
              <div className="task-selector-search">
                <input
                  type="search"
                  value={taskSearch}
                  onChange={(event) => setTaskSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') fetchAllTasks({ q: taskSearch, offset: 0 });
                  }}
                  placeholder={t('places.searchTasksPlaceholder')}
                  disabled={isCreatingPlaceTask}
                />
                <button onClick={() => fetchAllTasks({ q: taskSearch, offset: 0 })} disabled={loadingTasks || isCreatingPlaceTask}>
                  {t('common.search')}
                </button>
              </div>
              {taskSelectorError && <p className="task-selector-state error">{taskSelectorError}</p>}
              {loadingTasks && visibleSelectorTasks.length === 0 ? (
                <div className="spinner-container"><div className="spinner"></div></div>
              ) : visibleSelectorTasks.length > 0 ? (
                <div className="existing-tasks-grid">
                  {visibleSelectorTasks.map(task => {
                    const isCurrentPlace = String(task.place_id) === String(currentPlace?.id);
                    const placeName = task.place?.name || places.find(p => String(p.id) === String(task.place_id))?.name;
                    return (
                      <button
                        key={task.id}
                        className={`task-card-selector ${isCurrentPlace ? 'already-linked' : ''}`}
                        data-agent-task-title={task.main_task || ""}
                        data-agent-action="assign-task-to-place"
                        onClick={() => handleAssignTask(task)}
                        disabled={isCurrentPlace || loadingTasks || isCreatingPlaceTask}
                      >
                        <span className="task-title-mini">{task.main_task}</span>
                        <span className="task-card-meta">
                          {isCurrentPlace
                            ? t('places.alreadyInThisPlace')
                            : task.place_id
                              ? t('places.taskIn', { place: placeName || t('places.otherPlace') })
                              : t('places.withoutPlace')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-tasks-msg">{taskSearch ? t('places.noTasksMatch') : t('places.noOtherTasks')}</p>
              )}
              {selectorHasMore && (
                <button
                  className="btn-load-more-tasks"
                  onClick={() => fetchAllTasks({ q: taskSearch, offset: visibleSelectorTasks.length, append: true })}
                  disabled={loadingTasks}
                >
                  {loadingTasks ? t('common.loading') : t('common.loadMore')}
                </button>
              )}
            </div>
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => setShowTaskSelector(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showTrashModal && (
        <div id="places-trash-modal" className="modal-overlay">
          <div className="trash-place-modal glass-modal">
            <h2>{t('places.trashTitle')}</h2>
            <p className="modal-subtitle">
              {t('places.trashDescription')}
            </p>
            <div className="trash-places-list">
              {loadingDeletedPlaces ? (
                <div className="spinner-container"><div className="spinner"></div></div>
              ) : deletedPlaces.length > 0 ? (
                deletedPlaces.map(place => (
                  <div key={place.id} className="trash-place-item">
                    <div>
                      <strong>{place.name}</strong>
                      {place.ai_subtitle && <span>{place.ai_subtitle}</span>}
                    </div>
                    <button className="btn-restore-place" data-agent-action="restore-place" data-agent-place-name={place.name} onClick={() => handleRestorePlace(place)}>
                      {t('places.restore')}
                    </button>
                  </div>
                ))
              ) : (
                <p className="empty-tasks-msg">{t('places.trashEmpty')}</p>
              )}
            </div>
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => setShowTrashModal(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="create-place-modal glass-modal">
            <h2>{t('places.createTitle')}</h2>
            <p className="modal-subtitle">
              {t('places.createDescription')}
            </p>
            <div className="input-group">
              <input
                id="place-create-input"
                type="text"
                placeholder={t('places.createPlaceholder')}
                value={newPlaceName}
                onChange={(e) => setNewPlaceName(e.target.value)}
                autoFocus
                disabled={isCreating}
              />
            </div>
            {isCreating && (
              <div className="loading-area">
                <div className="spinner"></div>
                <span>{t('places.paintingReality')}</span>
              </div>
            )}
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
                {t('common.cancel')}
              </button>
              <button id="btn-create-place" className="btn-confirm gradient-btn" onClick={handleCreatePlace} disabled={isCreating || !newPlaceName.trim()}>
                {isCreating ? t('common.generating') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TareasVisuales;
